<template>
  <mf-card-container v-model="search" class="mt-5" :title="title" use-search placeholder="Buscar por CPF/CNPJ">
    <template v-slot:filters>
      <mf-tooltip :disabled="hasPiiAccess" max-width="250" top>
        <template #activator="{ on }">
          <span v-on="on">
            <export-data :disabled="!hasPiiAccess" class="mx-2" :data-fetcher="exportCsv" filename="compras_nao_creditadas" ghost />
          </span>
        </template>
        <span>Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso.</span>
      </mf-tooltip>
    </template>
    <paginated-data-table
      :key="'uncreditedPoints-' + skipQuery"
      ref="uncreditedPoints"
      :headers="headers"
      :search="search"
      :query="query"
      :custom-items-per-page="10"
      :query-variables="queryVariables"
      :csv-headers="customCsvHeaders"
      :get-csv-data-shape="getCsvDataShape"
      :queryName="cashbackOrPointsQuery"
      :queryMainPropertyName="'items'"
      :graphql-uri="cashbackMicroservice"
      :skip="skipQuery"
      csvFilename="compras_nao_creditadas"
      export-all-result
      :export-all-result-incremental-page-size="100"
      @update-options="updateVariables"
    >
      <template v-slot:header.uncredited_cashback="{ header }">
        <div class="d-flex align-center">
          <span class="header-text">{{ header.text }}</span>
          <v-tooltip bottom max-width="192px">
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <mf-icon class="info-icon" small color="cinza-0">info</mf-icon>
              </span>
            </template>
            <span> {{ tooltipText }} </span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:item="{ item }">
        <tr>
          <td :class="{ 'texto-corrido': !hasPiiAccess }">
            <router-link
              v-if="hasPiiAccess"
              class="link link-color text-corrido"
              :to="{ path: `/clientes/detalhes-cliente/${item.document_no}` }"
              target="_blank"
            >
              {{ item.document_no | cpfCnpj }}
            </router-link>
            <v-tooltip v-else max-width="250" top content-class="truncated-tooltip">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  {{ item.document_no }}
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </td>
          <td class="texto-corrido">
            <v-tooltip max-width="250" top content-class="truncated-tooltip" :disabled="hasPiiAccess">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  {{ item.name }}
                </span>
              </template>
              <span>Você precisa ter acessos a dados sensíveis para poder ver essa informação.</span>
            </v-tooltip>
          </td>
          <td class="texto-corrido">{{ DateTime.fromISO(item.purchase_date).setZone('UTC').toFormat('dd/MM/yyyy') }}</td>
          <td class="texto-corrido">{{ item.purchases_over_limit }}</td>
          <td class="texto-corrido">{{ purchasesValue(item) }}</td>
          <td>{{ item.created_at }}</td>
        </tr>
      </template>
    </paginated-data-table>
  </mf-card-container>
</template>

<script>
import { QUERY_GET_PURCHASES_UNCREDITED_POINTS } from '../../../../graphql'
import { DateTime } from 'luxon'
import PaginatedDataTable from '@/components/PaginatedDataTable'
import cpfCnpj from '@/filters/cpfCnpj.js'
import localeString from '@/filters/localeString.js'
import ExportData from '@/components/export-data/ExportData.vue'

export default {
  name: 'VoucherPurchasesWithoutPoints',
  components: {
    PaginatedDataTable,
    ExportData,
  },
  data: () => ({
    search: '',
    maxRecords: 0,
    cpfCnpj: cpfCnpj,
    DateTime: DateTime,
    locale: localeString,
    queryVariables: {
      sort: ['purchase_date'],
      sortDirection: ['DESC'],
    },
  }),
  computed: {
    query() {
      return QUERY_GET_PURCHASES_UNCREDITED_POINTS
    },
    customCsvHeaders() {
      return ['cpf/cnpj', 'consumidor', 'data da compra', 'quantidade de compras além do limite', `pontos não creditados`]
    },
    title() {
      return `Compras que não creditaram pontos`
    },
    pointsHeaderUppercase() {
      return 'Pontos'
    },
    cashbackMicroservice() {
      return ''
    },
    cashbackOrPointsQuery() {
      return 'purchasesUncreditedPoints'
    },
    tooltipText() {
      return `Quantidade de pontos que esse consumidor receberia nas compras fora do limite feitas nesse dia.`
    },
    headers() {
      return [
        {
          text: 'CPF/CNPJ',
          value: 'document_no',
          width: '164px',
          sortable: true,
          isDefault: true,
        },
        {
          text: 'Consumidor',
          width: '128px',
          value: 'name',
          sortable: false,
          isDefault: true,
        },
        {
          text: 'Data da compra',
          value: 'purchase_date',
          sortable: true,
          isDefault: true,
        },
        {
          text: 'Quantidade de compras além do limite',
          value: 'purchases_over_limit',
          sortable: true,
          isDefault: true,
        },
        {
          text: `${this.pointsHeaderUppercase} não creditados`,
          value: 'uncredited_cashback',
          sortable: false,
          isDefault: true,
          width: '200px',
        },
      ]
    },
    skipQuery() {
      return false
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
  },
  methods: {
    updateVariables(newQueryOptions) {
      const { sortBy, sortDesc } = newQueryOptions

      if (sortBy.length && sortDesc.length) {
        this.queryVariables.sort = sortBy
        this.queryVariables.sortDirection = sortDesc[0] ? ['DESC'] : ['ASC']
      }
    },
    exportCsv() {
      return this.$refs.uncreditedPoints.exportCsv()
    },
    getCsvDataShape(item) {
      return {
        document_no: this.cpfCnpj(item.document_no),
        name: item.name,
        purchase_date: DateTime.fromISO(item.purchase_date).setZone('UTC').toFormat('dd/MM/yyyy'),
        purchases_over_limit: item.purchases_over_limit,
        uncredited_points: this.purchasesValue(item),
      }
    },
    purchasesValue(item) {
      return item.uncredited_points
    },
  },
}
</script>

<style lang="scss">
.header-text {
  width: 140px;
  display: inline-block;
}

.info-icon {
  display: table-cell;
  vertical-align: middle;
}

.link-color {
  color: $marca-0-l40 !important;
}
</style>
